import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronRight, ChevronDown, Search, Users, ChevronsDownUp, ChevronsUpDown, Network } from "lucide-react";

const DEPT_ACCENT = {
  Executive: "#1d4ed8", Engineering: "#0ea5e9", Sales: "#f59e0b", Finance: "#10b981",
  Support: "#8b5cf6", Marketing: "#ec4899", People: "#14b8a6", Consulting: "#ef4444",
  IT: "#6366f1", Product: "#f97316", Operations: "#64748b", Legal: "#a16207",
};

function accent(d) {
  return DEPT_ACCENT[d] || "#71717a";
}

function subtreeMatches(node, q) {
  if (!q) return true;
  const hit = (node.name || "").toLowerCase().includes(q)
    || (node.title || "").toLowerCase().includes(q)
    || (node.department || "").toLowerCase().includes(q);
  return hit || node.children.some((c) => subtreeMatches(c, q));
}

// Flatten the tree into rows (depth-first), skipping children of collapsed nodes and branches
// that don't match the active search. Plain recursion — no self-referential JSX component.
function flattenRows(nodes, q, collapsed, depth, out) {
  const sorted = [...nodes].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  for (const node of sorted) {
    if (!subtreeMatches(node, q)) continue;
    const visibleKids = node.children.filter((c) => subtreeMatches(c, q));
    const hasKids = visibleKids.length > 0;
    const isOpen = q ? true : !collapsed.has(node.id);
    out.push({ node, depth, hasKids, isOpen });
    if (hasKids && isOpen) flattenRows(visibleKids, q, collapsed, depth + 1, out);
  }
  return out;
}

export default function OrgChart() {
  const { t } = useLang();
  const nav = useNavigate();
  const [data, setData] = useState({ tree: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(() => new Set());

  useEffect(() => {
    api.get("/org/tree").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const rows = useMemo(() => flattenRows(data.tree, q, collapsed, 0, []), [data.tree, q, collapsed]);

  const toggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const collectIds = (nodes, acc) => {
    for (const n of nodes) { if (n.children.length) { acc.push(n.id); collectIds(n.children, acc); } }
    return acc;
  };
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(collectIds(data.tree, [])));

  return (
    <>
      <PageHeader title={t("nav.orgChart")} subtitle={t("org.subtitle").replace("{n}", data.total)}>
        <Button variant="outline" size="sm" onClick={expandAll} data-testid="org-expand-all" className="rounded-sm">
          <ChevronsUpDown size={15} className="mr-1.5" /> {t("org.expandAll")}
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll} data-testid="org-collapse-all" className="rounded-sm">
          <ChevronsDownUp size={15} className="mr-1.5" /> {t("org.collapseAll")}
        </Button>
      </PageHeader>

      <div className="p-6 md:p-10">
        <div className="relative mb-5 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("org.search")} className="rounded-sm border-zinc-300 pl-9" data-testid="org-search" />
        </div>

        {loading ? (
          <div className="py-20 text-center text-sm text-zinc-400" data-testid="org-loading">{t("common.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-20 text-center text-zinc-400" data-testid="org-empty">
            <Network size={28} /> <span className="text-sm">{t("org.empty")}</span>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-zinc-200 bg-white" data-testid="org-tree">
            {rows.map((row) => {
              const node = row.node;
              const disabled = node.accountEnabled === false || node.status === "disabled";
              const col = accent(node.department);
              const isHit = q && ((node.name || "").toLowerCase().includes(q)
                || (node.title || "").toLowerCase().includes(q)
                || (node.department || "").toLowerCase().includes(q));
              const rowStyle = { paddingLeft: 12 + row.depth * 26, borderLeftColor: col };
              const rowCls = "group flex items-center gap-2 border-l-2 py-1.5 pr-3 transition-colors hover:bg-zinc-50 " + (isHit ? "bg-amber-50" : "");
              const nameCls = "truncate text-sm font-semibold group-hover:text-primary " + (disabled ? "text-zinc-400 line-through" : "text-zinc-900");
              return (
                <div key={node.id} className={rowCls} style={rowStyle} data-testid={"org-node-" + node.id}>
                  {row.hasKids ? (
                    <button onClick={() => toggle(node.id)} className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-zinc-500 hover:bg-zinc-200" data-testid={"org-toggle-" + node.id} aria-label="toggle">
                      {row.isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </button>
                  ) : (
                    <span className="h-5 w-5 shrink-0" />
                  )}
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: disabled ? "#d4d4d8" : "#22c55e" }} />
                  <button onClick={() => nav("/employees/" + node.id)} className="flex min-w-0 flex-1 items-baseline gap-2 text-left" data-testid={"org-open-" + node.id}>
                    <span className={nameCls}>{node.name}</span>
                    {node.title ? <span className="hidden truncate text-xs text-zinc-500 sm:inline">· {node.title}</span> : null}
                    {node.department ? (
                      <span className="hidden shrink-0 items-center rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider md:inline" style={{ color: col, background: col + "14" }}>
                        {node.department}
                      </span>
                    ) : null}
                  </button>
                  {node.reportCount > 0 ? (
                    <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-bold text-zinc-600" data-testid={"org-reports-" + node.id}>
                      <Users size={11} /> {node.reportCount}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
